import React, { useState, useEffect } from 'react'

import { PaymentOption } from '../paymentOptions'
import { CurrencyInput } from '../../../../components/inputs'
import PaymentOptionsRadio from './PaymentOptionsRadio'
import LoaderButton from '../../../../components/loaderButton'
import numeral from 'numeral'
import { ProductPackage } from '../../../../core/types'
import { useSelector } from 'react-redux'
import { selectBusinessConfig } from '../../../../reducers'

export interface PaymentPlanProvider {
  value: string
  label: string
  enabled: boolean
}

export interface PaymentDetailsProps {
  showPaymentDetails: boolean
  downPayment: string
  numberPayment: number
  setNumberPayment(numberPayment: number): void
  paymentOptions: PaymentOption[]
  children: React.ReactNode
  onEnterPress: (payment_plan_provider: string) => void
  onCurrencyChange: (value: string) => void
  isDownPaymentNextButtonDisabled: boolean
  paymentLimits: { max: number }
  productPackage: ProductPackage
  isMonthlyMembership?: boolean
}

export function PaymentDetails({
  children,
  downPayment,
  numberPayment,
  paymentOptions,
  showPaymentDetails,
  setNumberPayment,
  onEnterPress,
  onCurrencyChange,
  isDownPaymentNextButtonDisabled,
  paymentLimits,
  productPackage,
  isMonthlyMembership = false,
}: PaymentDetailsProps) {
  const [showDetailsLocal, setShowDetailsLocal] = useState(
    showPaymentDetails || isMonthlyMembership
  )
  const businessConfig = useSelector(selectBusinessConfig)

  // Providers and their labels come from GET /api/v3/configuration. They used to
  // be hardcoded <option> tags here, so enabling a provider for an org meant a
  // frontend deploy, and every org saw every provider.
  const paymentPlan = businessConfig?.payment_plan || {}
  const providers = (paymentPlan.providers || []).filter(
    (p: PaymentPlanProvider) => p.enabled
  )

  const isOffered = (value?: string | null) =>
    !!value && providers.some((p: PaymentPlanProvider) => p.value === value)

  // A monthly membership is always EFS/Ensurety -- the only provider that
  // supports it. Otherwise seed from the package: the backend already stored a
  // provider if the agent picked one, and defaulting to a literal 'ensurety'
  // both lost that choice on re-render and was wrong for an org that does not
  // offer Ensurety.
  //
  // Only a provider the select can show is used. A stored one that is no longer
  // enabled (historical flopay_walco packages became walco, which is off by
  // default) would leave the select blank while Next submitted the hidden
  // value, so it falls through to the org default.
  const storedProvider =
    productPackage?.paymentPlanProviderSource ||
    productPackage?.payment_plan_provider_source
  const initialProvider = isMonthlyMembership
    ? 'ensurety'
    : [storedProvider, paymentPlan.default_provider, providers[0]?.value].find(
        isOffered
      ) || ''
  const [provider, setProvider] = useState(initialProvider)

  useEffect(() => {
    // A monthly membership skips the provider step entirely, so go straight to
    // the details.
    setShowDetailsLocal(showPaymentDetails || isMonthlyMembership)
  }, [showPaymentDetails, isMonthlyMembership])

  // Config and package both arrive asynchronously; adopt a real value once one
  // shows up, but never clobber a choice the agent has already made. A monthly
  // membership is pinned to Ensurety whatever was stored. A value the select
  // cannot show is replaced, so what Next submits is always what is on screen.
  useEffect(() => {
    if (isMonthlyMembership) {
      if (provider !== 'ensurety') setProvider('ensurety')
    } else if (!isOffered(provider) && provider !== initialProvider) {
      setProvider(initialProvider)
    }
  }, [initialProvider, provider, isMonthlyMembership])

  // With no enabled provider (config missing, failed, or empty) there is
  // nothing to submit, so Next is disabled and the reason shown instead of an
  // empty select.
  const noProviders = !isMonthlyMembership && providers.length === 0
  const isNextDisabled = isDownPaymentNextButtonDisabled || !provider

  const handleSubmit = () => {
    if (!isNextDisabled) onEnterPress(provider)
  }

  const maxValue = numeral(paymentLimits.max).format('$0,0.00')

  // If the downpayment has already been paid (status is "paid")
  // then disable the downpayment input & set to paid amount
  let downPaymentMessage = `Enter down payment amount (up to ${maxValue})`
  let downPaymentValue = downPayment
  let downPaymentDisabled = false
  if (productPackage.status.code === 'payment_succeeded' || productPackage.status.code === 'product_remitted_error') {
    downPaymentMessage =
      "This down payment has already been paid and can't be changed"
    downPaymentValue = productPackage.down_payment
    downPaymentDisabled = true
  }

  return (
    <>
      {showDetailsLocal ? (
        <div>
          <div className="refi-payment-options__upper-content">
            <div className="refi-payment-options__down-payment-left-section">
              <span
                className="refi-payment-options__section-title"
                style={{ cursor: isMonthlyMembership ? 'default' : 'pointer' }}
                onClick={() =>
                  !isMonthlyMembership && setShowDetailsLocal(false)
                }
              >
                Down Payment
              </span>
            </div>
            <span>$ {downPaymentValue}</span>
          </div>
          <hr />
          <div className="refi-payment-options__lower-content">
            <span className="refi-payment-options__section-title">
              Payment options
            </span>
            <div className="refi-payment-options__radio-buttons-group">
              {paymentOptions.map((paymentOption, index) => {
                return (
                  <PaymentOptionsRadio
                    isChecked={numberPayment === paymentOption.numberPayment}
                    paymentOptions={paymentOption}
                    downValue={downPaymentValue}
                    key={index}
                    isMonthlyMembership={isMonthlyMembership}
                    selectOption={() =>
                      setNumberPayment(paymentOption.numberPayment)
                    }
                  />
                )
              })}
            </div>
          </div>
          <hr />
          {numberPayment ? children : null}
        </div>
      ) : (
        <div className="refi-payment-options__down-payment-left-section">
          <span className="refi-payment-options__section-title">
            Payment Plan Provider
          </span>
          {noProviders ? (
            <p
              className="refi-payment-options__input-container"
              data-testid="no-payment-plan-provider"
            >
              No payment plan provider is enabled for this organization.
              Contact support to configure one.
            </p>
          ) : (
            <select
              className="refi-payment-options__input-container"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              data-testid="payment-plan-provider"
            >
              {providers.map((p: PaymentPlanProvider) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          )}
          <span className="refi-payment-options__section-title">
            Down Payment
          </span>
          <label htmlFor="">{downPaymentMessage}</label>
          <CurrencyInput
            className="refi-payment-options__input-container"
            value={downPaymentValue}
            onChange={onCurrencyChange}
            onEnter={handleSubmit}
            disabled={downPaymentDisabled}
          />
          <div className="form__group refi-app__form-buttons">
            <LoaderButton
              className="button button-drive-america"
              onClick={handleSubmit}
              disabled={isNextDisabled}
              data-testid="next-button"
            >
              Next
            </LoaderButton>
          </div>
        </div>
      )}
    </>
  )
}

export default PaymentDetails
